import React, { useEffect, useRef, useState } from "react";

/**
 * A Google Maps picker: search box (using the NEW Places API's
 * PlaceAutocompleteElement — the legacy Autocomplete class is no longer
 * enableable on new Google Cloud projects), a "use my current location"
 * button, and a draggable pin. Every selection method resolves to a
 * readable address via reverse geocoding — calls onChange({ lat, lng, address }).
 *
 * Requires the Google Maps JS script to already be loaded on the page —
 * see public/index.html. The script's &libraries=places parameter is still
 * fine to keep; this component loads the places library itself via
 * importLibrary either way.
 */
export default function LocationMapPicker({ onChange, initialLat = 6.9271, initialLng = 79.8612 }) {
  const mapDivRef = useRef(null);
  const searchContainerRef = useRef(null); // holds the new PlaceAutocompleteElement
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const geocoderRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [notLoaded, setNotLoaded] = useState(false);
  const [locationError, setLocationError] = useState("");

  const reverseGeocode = (lat, lng, callback) => {
    if (!geocoderRef.current) {
      callback("");
      return;
    }
    geocoderRef.current.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results && results[0]) {
        callback(results[0].formatted_address);
      } else {
        callback("");
      }
    });
  };

  useEffect(() => {
    if (!window.google || !window.google.maps) {
      setNotLoaded(true);
      return;
    }

    const map = new window.google.maps.Map(mapDivRef.current, {
      center: { lat: initialLat, lng: initialLng },
      zoom: 14,
    });
    mapRef.current = map;
    geocoderRef.current = new window.google.maps.Geocoder();

    const marker = new window.google.maps.Marker({
      position: { lat: initialLat, lng: initialLng },
      map,
      draggable: true,
    });
    markerRef.current = marker;

    const emitChange = (lat, lng, address) => {
      onChange({ lat, lng, address: address || "" });
    };

    marker.addListener("dragend", () => {
      const pos = marker.getPosition();
      reverseGeocode(pos.lat(), pos.lng(), (address) => emitChange(pos.lat(), pos.lng(), address));
    });

    map.addListener("click", (e) => {
      marker.setPosition(e.latLng);
      reverseGeocode(e.latLng.lat(), e.latLng.lng(), (address) => emitChange(e.latLng.lat(), e.latLng.lng(), address));
    });

    // --- NEW Places API search box (replaces the old Autocomplete class) ---
    (async () => {
      const { PlaceAutocompleteElement } = await window.google.maps.importLibrary("places");

      const placeAutocomplete = new PlaceAutocompleteElement();
      placeAutocomplete.style.width = "100%";
      searchContainerRef.current.innerHTML = ""; // clear in case of re-render
      searchContainerRef.current.appendChild(placeAutocomplete);

      placeAutocomplete.addEventListener("gmp-select", async ({ placePrediction }) => {
        const place = placePrediction.toPlace();
        await place.fetchFields({ fields: ["location", "formattedAddress", "displayName"] });

        if (!place.location) return;
        const lat = place.location.lat();
        const lng = place.location.lng();

        map.panTo({ lat, lng });
        map.setZoom(16);
        marker.setPosition({ lat, lng });
        emitChange(lat, lng, place.formattedAddress || place.displayName);
      });

      setReady(true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const useCurrentLocation = () => {
    setLocationError("");

    if (!navigator.geolocation) {
      setLocationError("Your browser doesn't support location access.");
      return;
    }
    if (!mapRef.current || !markerRef.current) {
      setLocationError("Map is still loading — try again in a moment.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        mapRef.current.panTo({ lat: latitude, lng: longitude });
        mapRef.current.setZoom(16);
        markerRef.current.setPosition({ lat: latitude, lng: longitude });
        reverseGeocode(latitude, longitude, (address) => {
          onChange({ lat: latitude, lng: longitude, address });
        });
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) {
          setLocationError("Location access was denied. Allow it in your browser settings, or search/click on the map instead.");
        } else {
          setLocationError("Couldn't get your location. Try searching or clicking on the map instead.");
        }
      }
    );
  };

  if (notLoaded) {
    return (
      <div style={{ padding: 12, background: "#fef2f2", borderRadius: 8, color: "#dc2626", fontSize: 13 }}>
        Google Maps didn't load. Check that the Maps script tag with your API key is in public/index.html.
      </div>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={useCurrentLocation}
        style={{ marginBottom: 8, padding: "6px 12px", border: "1px solid #ccc", borderRadius: 6, background: "#fff", cursor: "pointer" }}
      >
        📍 Use my current location
      </button>
      {locationError && (
        <p style={{ fontSize: 12, color: "#dc2626", marginTop: -4, marginBottom: 8 }}>{locationError}</p>
      )}
      {/* The PlaceAutocompleteElement renders itself inside this container */}
      <div ref={searchContainerRef} style={{ marginBottom: 8 }} />
      <div
        ref={mapDivRef}
        style={{ width: "100%", height: 300, borderRadius: 8, border: "1px solid #ddd", opacity: ready ? 1 : 0.5 }}
      />
      <p style={{ fontSize: 12, color: "#666", marginTop: 6 }}>
        Use your current location, search above, or click/drag the pin directly on the map to fine-tune the exact spot.
      </p>
    </div>
  );
}